import { LockOutlined } from '@mui/icons-material'
import { darken, List } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import type { BlockOverlayProps } from 'components/BlockOverlay'
import { GroupCardAvatar } from 'components/cards/styled'
import PopupSection from 'components/popup/PopupSection'
import PopupSectionRow from 'components/popup/PopupSectionRow'
import CalendarSelectPopup, {
  CalendarSectionItem
} from 'components/popups/CalendarSelectPopup'
import { SelectableItem } from 'components/popups/SelectItemWithSearchPopup'
import { useCalendarSync } from 'components/providers/CalendarSyncProdivder'
import { useNotification } from 'components/providers/NotificationProvider'
import HorizontalChipsBar, {
  ChipData
} from 'components/sidebar/HorizontalChips'
import { IEventDetails } from 'hooks/useEventDetails'
import { t } from 'i18next'
import { GroupPrivacy } from 'model/models/Group'
import { useEffect, useState } from 'react'
import { useWatch } from 'react-hook-form'
import Database from 'services/db/Database'
import { localEventByLocalId } from 'services/db/Queries/Event'
import { localGroupById } from 'services/db/Queries/GroupQueries'
import LocalStorage from 'services/LocalStorage'
import Logger from 'services/Logger'
import { DocType, EventDoc, TaskType, UserSettingsDoc } from 'types/api/changes'
import { ICalendar } from 'types/google_events'
import { Model } from 'types/model'
import { LocalStorageKey } from 'types/services/localStorage'
import { defaultConvertedCalendarPhoto } from 'utils/api/groups'
import { isUserActiveInGroup } from 'utils/api/groups'
import { is24meApp } from 'utils/appType'
import { CurrentConvertedCalendars } from 'utils/google_events'
import { isConvertedGroup, isConvertedGroupId } from 'utils/groups'
import { removePrefix } from 'utils/object'

import channelDefaultPhoto from '../../../assets/icons/channel_calendar.svg'
import { ContentProps, prepareCalendars, prepareSections } from './EventPopup'

export interface CalendarSectionProps extends ContentProps, BlockOverlayProps {
  userSettings: Model<UserSettingsDoc> | UserSettingsDoc
  setGroupID: IEventDetails['setGroupID']
  setThirdPartyId: (id: string) => void
  thirdParty: string
  selectedGroupId?: string
  setType: (type: DocType) => void
  eventHook: IEventDetails
  SupplementaryGroupsIDs?: string[]
  setRegularEventColor?: (color: string) => void
}

export default function CalendarSection(props: CalendarSectionProps) {
  const theme = useTheme()
  const calendarSync = useCalendarSync()
  const { showNotification } = useNotification()
  const isExistingEvent = (props.event._id?.length ?? 0) > 0
  const currentGroupIsConvertedCalendar =
    isConvertedGroup(props.group) ||
    props.event.TaskType === TaskType.personalEvent ||
    props.eventHook.getValues('TaskType') === TaskType.personalEvent

  const groupOrCalendarId = props.selectedGroupId

  const [calendarTitle, onCalendarTitle] = useState<string>(
    currentGroupIsConvertedCalendar
      ? calendarSync.calendarTitle(props.thirdParty, props.group)
      : (props.group?.Name ?? '')
  )

  useEffect(() => {
    Logger.blue(
      'EventPOpup:CalendarSection:useEffect',
      props.thirdParty,
      props.selectedGroupId
    )
    if (is24meApp()) {
      localGroupById(props.eventHook.getValues('GroupID') ?? '').then(
        (localGroup) => {
          onCalendarTitle(
            currentGroupIsConvertedCalendar
              ? calendarSync.calendarTitle(props.thirdParty, localGroup)
              : (props.group?.Name ?? '')
          )
        }
      )
    }
  }, [props.thirdParty, props.selectedGroupId])

  const [showCalendarPicker, onShowCalendarPicker] = useState(false)

  const applyCalendarSelection = (
    calendar: SelectableItem,
    newGroupId: string,
    newOwnerId: string,
    newTaskType: TaskType,
    newColor: string | undefined,
    isSharedCalendar: boolean,
    options?: {
      writeOwnerLocally?: boolean
      writeGroupLocally?: boolean
    }
  ) => {
    onCalendarTitle(calendar.title)

    if (isSharedCalendar) {
      props.eventHook.setTaskType(TaskType.personalEvent)
      props.eventHook.setReminders([
        ...(props.eventHook
          .getValues('Reminder')
          ?.filter((r) => r.isRelativeReminder === '1') || [])
      ])
    } else {
      props.eventHook.setTaskType(TaskType.meet)
    }

    props.eventHook.setOwnerId(newOwnerId, {
      refreshCalendar: false,
      writeLocal: options?.writeOwnerLocally ?? false
    })
    props.setThirdPartyId(`${calendar.id}`)
    props.setGroupID(newGroupId ?? '', {
      selectInSidebar: true,
      refreshCalendar: true,
      skipLocalWrite: !(options?.writeGroupLocally ?? false),
      eventOverrides: {
        Color: newColor,
        OwnerID: newOwnerId,
        PersonalOwnerId: newOwnerId,
        TaskType: newTaskType,
        ThirdPartyID: `${calendar.id}`
      }
    })
  }

  const onCalendarPick = (calendar: SelectableItem) => {
    const isThirdPartyCalendar =
      (calendar as ICalendar)?.primaryCalendarEmail !== undefined

    if (isExistingEvent && isThirdPartyCalendar) {
      showNotification(t('changeNotSupported'))
      return
    }

    onShowCalendarPicker(false)
    Logger.orange('CalendarSection:', calendar)
    const isSharedCalendar = isThirdPartyCalendar
    const thirdPartyCalendar = CurrentConvertedCalendars.find(
      (userCalendar) => {
        return userCalendar.id === removePrefix(calendar.id ?? '')
      }
    )
    Logger.orange('CalendarSection:thirdPartyCalendar', thirdPartyCalendar)
    const newGroupId = isSharedCalendar
      ? (calendar.localId ?? calendar.id)
      : calendar.id
    const newOwnerId = thirdPartyCalendar
      ? `${(calendar as ICalendar).primaryCalendarEmail}`
      : (LocalStorage.get(LocalStorageKey.USER_ID) ?? '')
    const newTaskType = isSharedCalendar
      ? TaskType.personalEvent
      : TaskType.meet
    const newColor = thirdPartyCalendar?.backgroundColor
      ? darken(thirdPartyCalendar.backgroundColor, 0.15)
      : newTaskType === TaskType.personalEvent
        ? props.event.Color
        : undefined
    Logger.orange('CalendarSection:calendar', newGroupId)

    if (isExistingEvent) {
      if (thirdPartyCalendar) {
        props.eventHook.setSelectedColor(newColor ?? '')
      }

      applyCalendarSelection(
        calendar,
        newGroupId ?? '',
        newOwnerId,
        newTaskType,
        newColor,
        isSharedCalendar,
        {
          writeOwnerLocally: false,
          writeGroupLocally: false
        }
      )
      return
    }

    Database.write(async () => {
      if (props.event.syncStatus === 'deleted') return
      ;(
        await localEventByLocalId(
          props.event.id?.length ? props.event.id : (props.fc_id ?? '')
        )
      )?.update((event: EventDoc) => {
        Logger.blue('CalendarSection:UpdateColor', thirdPartyCalendar)
        if (thirdPartyCalendar) {
          event.Color = newColor
          props.eventHook.setSelectedColor(newColor ?? '')
        }

        event.GroupID = newGroupId ?? ''
        event.OwnerID = newOwnerId
        event.TaskType = newTaskType
        event.ThirdPartyID = `${calendar.id}`
        event.PersonalOwnerId = newOwnerId
      })
    }).then(() => {
      applyCalendarSelection(
        calendar,
        newGroupId ?? '',
        newOwnerId,
        newTaskType,
        newColor,
        isSharedCalendar,
        {
          writeOwnerLocally: false,
          writeGroupLocally: false
        }
      )
    })
  }

  const showCalendarPickerForGoogleEvent = () => {
    onShowCalendarPicker(true)
  }

  const [calendars, onCalendars] = useState<SelectableItem[]>([])
  const [inheritedCalendars, setInheritedCalendars] = useState<
    SelectableItem[]
  >([])
  const [resolvedMainTitle, setResolvedMainTitle] = useState<string>('')
  // WatermelonDB JSON getters return a new object on every read. Depend on
  // the IDs themselves so resolving their titles cannot restart this effect.
  const inheritedGroupIdsJson = JSON.stringify(
    Object.keys(props.event.inheritedGroups ?? {})
  )

  const supplementaryGroupIds =
    (useWatch({
      control: props.eventHook.control as any,
      name: 'SupplementaryGroupsIDs' as any,
      defaultValue: props.event.SupplementaryGroupsIDs as string[]
    }) as string[] | undefined) ?? []

  //load groups for the list
  useEffect(() => {
    prepareCalendars(props.account, groupOrCalendarId).then((data) => {
      onCalendars(data)
    })
  }, [props.event, props.thirdParty])

  useEffect(() => {
    let active = true
    const inheritedGroupIds: string[] = JSON.parse(inheritedGroupIdsJson)
    Promise.all(
      inheritedGroupIds.map((groupId) => localGroupById(groupId))
    ).then((groups) => {
      if (!active) return
      setInheritedCalendars(
        inheritedGroupIds.map((groupId, index) => ({
          id: groupId,
          title: groups[index]?.Name ?? groupId,
          photo: groups[index]?.Photo,
          group: groups[index],
          notClickable: true,
          notClickableReason: t('linkedCalendar'),
          inherited: true,
          rightIcon: <LockOutlined fontSize="small" />
        }))
      )
    })
    return () => {
      active = false
    }
  }, [props.event, inheritedGroupIdsJson])

  // Resolve main group title from local DB as a fallback when props.group is missing.
  useEffect(() => {
    if (!groupOrCalendarId) return
    localGroupById(groupOrCalendarId).then((g) => {
      setResolvedMainTitle(g?.Name ?? '')
    })
  }, [groupOrCalendarId])

  // Titles resolved against locally-loaded groups (calendars) or local DB.
  const [supplementaryTitlesResolved, setSupplementaryTitlesResolved] =
    useState<string[]>([])
  useEffect(() => {
    Promise.all(
      supplementaryGroupIds.map(async (groupId) => {
        const fromCalendars = calendars.find((c) => c.id === groupId)?.title
        if (fromCalendars) return fromCalendars
        const local = await localGroupById(groupId)
        return local?.Name ?? ''
      })
    ).then((titles) =>
      setSupplementaryTitlesResolved(titles.filter((t) => Boolean(t)))
    )
  }, [supplementaryGroupIds.join(','), calendars])

  const inheritedGroupIds = new Set(
    inheritedCalendars.map((calendar) => calendar.id ?? '')
  )

  const inheritedItemsById = new Map(
    inheritedCalendars.map((item) => [item.id ?? '', item])
  )

  const preparedSections = prepareSections(
    calendars,
    true,
    groupOrCalendarId,
    props.SupplementaryGroupsIDs
  )

  const seenInheritedIds = new Set<string>()

  const sections: CalendarSectionItem[] = preparedSections
    .map((section) => {
      if (!isExistingEvent) {
        return {
          ...section,
          items: section.items.map((item) => {
            const inheritedItem = inheritedItemsById.get(item.id ?? '')
            if (inheritedItem) {
              seenInheritedIds.add(item.id ?? '')
              return {
                ...item,
                notClickable: true,
                notClickableReason: t('linkedCalendar'),
                inherited: true,
                rightIcon: <LockOutlined fontSize="small" />
              }
            }
            return item
          })
        }
      }

      // For existing events on personal/iCloud calendars, show only personal calendars
      // For existing events on shared calendars, show only shared calendars
      return {
        ...section,
        items: section.items
          .filter((item) => {
            if (inheritedGroupIds.has(item.id ?? '')) return true
            const isThirdPartyItem =
              (item as ICalendar)?.primaryCalendarEmail !== undefined
            return currentGroupIsConvertedCalendar
              ? isThirdPartyItem // If current event is on iCloud, keep only iCloud options
              : !isThirdPartyItem // If current event is shared, keep only shared options
          })
          .map((item) => {
            const inheritedItem = inheritedItemsById.get(item.id ?? '')
            if (inheritedItem) {
              seenInheritedIds.add(item.id ?? '')
              return {
                ...item,
                notClickable: true,
                notClickableReason: t('linkedCalendar'),
                inherited: true,
                rightIcon: <LockOutlined fontSize="small" />
              }
            }
            return item
          })
      }
    })
    .filter((section) => section.items.length > 0)

  // Any inherited calendars not already displayed in an existing section are
  // appended to the last section so they remain visible (no dedicated section).
  const remainingInherited = inheritedCalendars.filter(
    (item) => !seenInheritedIds.has(item.id ?? '')
  )
  if (remainingInherited.length > 0) {
    if (sections.length > 0) {
      const lastIdx = sections.length - 1
      sections[lastIdx] = {
        ...sections[lastIdx],
        items: [...sections[lastIdx].items, ...remainingInherited]
      }
    } else {
      sections.push({
        title: '',
        items: remainingInherited,
        alwaysVisible: true
      })
    }
  }

  Logger.blue('CalendarSection', groupOrCalendarId)

  const fallbackCalendar = !calendarTitle
    ? calendars.find(
        (c) => c.title && c.id && supplementaryGroupIds.includes(c.id)
      )
    : undefined

  const effectiveTitle =
    calendarTitle || fallbackCalendar?.title || resolvedMainTitle
  const effectiveId = calendarTitle
    ? groupOrCalendarId
    : (fallbackCalendar?.id ?? groupOrCalendarId)
  const effectiveGroup =
    effectiveId === groupOrCalendarId
      ? props.group
      : calendars.find((c) => c.id === effectiveId)?.group

  const existingSupplementaryIds = supplementaryGroupIds.filter(
    (groupId) => groupId !== effectiveId
  )

  const supplementaryTitles = existingSupplementaryIds
    .map(
      (groupId, index) =>
        calendars.find((value) => value.id === groupId)?.title ??
        supplementaryTitlesResolved[index]
    )
    .filter((title): title is string => Boolean(title))

  const inheritedTitles = inheritedCalendars
    .map((calendar) => calendar.title)
    .filter((title): title is string => Boolean(title))

  const combinedTitleParts = [
    effectiveTitle,
    ...supplementaryTitles,
    ...inheritedTitles
  ].filter((title): title is string => Boolean(title))

  const titleChips: ChipData[] = combinedTitleParts.map((title, index) => ({
    title,
    key: String(index)
  }))

  const currentUserId = LocalStorage.get(LocalStorageKey.USER_ID) ?? ''
  const shouldIndicatePrimary = effectiveGroup
    ? isUserActiveInGroup(effectiveGroup, currentUserId)
    : false
  const primaryChipKey = shouldIndicatePrimary ? titleChips[0]?.key : undefined

  const combinedTitle = combinedTitleParts.join(', ')

  const showChips =
    supplementaryGroupIds.length > 0 || inheritedCalendars.length > 0

  return (
    <CalendarSelectPopup
      group={props.group}
      account={props.account}
      eventHook={props.eventHook}
      onCalendarPick={onCalendarPick}
      calendars={sections}
      open={showCalendarPicker}
      setOpen={onShowCalendarPicker}
      SupplementaryGroupsIDs={props.SupplementaryGroupsIDs ?? []}
    >
      <PopupSection
        titlePadding={'0px'}
        isUiBlocked={props.isUiBlocked}
        blockedOverlayClick={props.blockedOverlayClick}
        title={`${t('calendar')}`}
      >
        <List disablePadding>
          {combinedTitle && effectiveId && (
            <PopupSectionRow
              key={effectiveId}
              iconClass="icon-big"
              actionBg
              flexGrowForRow={1}
              width="0px"
              icon={
                <GroupCardAvatar
                  asGroup
                  img={
                    calendars.find((value) => value.id === effectiveId)?.photo
                  }
                  name=""
                  size={24}
                  emptyPic={
                    isConvertedGroupId(effectiveId)
                      ? defaultConvertedCalendarPhoto(effectiveId)
                      : effectiveGroup?.PrivacyMode === GroupPrivacy.CHANNEL
                        ? (channelDefaultPhoto as any)
                        : (defaultConvertedCalendarPhoto(effectiveId) as any)
                  }
                />
              }
              title={
                showChips ? (
                  <HorizontalChipsBar
                    chips={titleChips}
                    selectedKey={primaryChipKey}
                    onChipSelect={() => {}}
                    chipClickable={false}
                    showScrollButtons={false}
                    containerStyle={{
                      padding: 0,
                      margin: 0,
                      width: '100%',
                      minWidth: 0
                    }}
                    chipsContainerStyle={{
                      padding: 0,
                      width: '100%',
                      minWidth: 0,
                      maxWidth: '100%'
                    }}
                    chipStyle={{
                      fontWeight: 400,
                      maxWidth: 'none',
                      overflow: 'visible',
                      textOverflow: 'clip'
                    }}
                  />
                ) : (
                  <div
                    style={{
                      display: 'inline-block',
                      maxWidth: '100%',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      maxHeight: '30px'
                    }}
                  >
                    {combinedTitle}
                  </div>
                )
              }
              overflow="hidden"
              action={showCalendarPickerForGoogleEvent}
              spacing={theme.spacing(0)}
              actionId="change-calendar-button"
            />
          )}
        </List>
      </PopupSection>
    </CalendarSelectPopup>
  )
}
